'use client';

import { useEffect } from 'react';
import dynamic from 'next/dynamic';
import { Box, VStack, Flex, Collapse, useDisclosure } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { PageContainer, PageContent } from 'components';

import { useResponsiveness } from 'hooks/useResponsiveness';
import { ExpandControl } from 'components/OutletsMap/ExpandControl';
import { useSearchPageStore } from '../useSearchPageStore';
import { Search } from './Search';
import { MapSection } from './MapSection';
import { Header } from './Header';
import { FiltersModal } from './FiltersModal';

const DesktopNavigation = dynamic(
  () =>
    import('components/Layout/components/DesktopNavigation/DesktopNavigation'),
  { ssr: false },
);

interface Props {
  categories: Category[];
  initialData: {
    networks: AdaptedNetwork[];
    stores: Paginated<AdaptedStore>;
  };
  initialParams: {
    near: string | null;
    categoryId: string | null;
    includeNetworks: boolean;
  };
}

export type SearchPageProps = Props;

export const SearchPage = (props: Props) => {
  const { initialData, categories, initialParams } = props;

  const { networks, stores } = initialData;

  const slicedNetworks = networks.slice(0, 4);

  const { isMobile } = useResponsiveness();

  const { isOpen, onToggle } = useDisclosure();

  const isMapOpen = useSearchPageStore(state => state.isMapOpen);
  const toggleMap = useSearchPageStore(state => state.toggleMap);
  const setIsLoading = useSearchPageStore(state => state.setIsLoading);
  const onNearChange = useSearchPageStore(state => state.onNearChange);
  const onSelectedCategoriesChange = useSearchPageStore(
    state => state.onSelectedCategoriesChange,
  );
  const onIncludeNetworksChange = useSearchPageStore(
    state => state.onIncludeNetworksChange,
  );

  useEffect(() => {
    const categoriesFilter = initialParams.categoryId?.split(',');

    if (categoriesFilter) onSelectedCategoriesChange(categoriesFilter);

    if (initialParams.near !== null) onNearChange(initialParams.near);

    if (initialParams.includeNetworks !== null) {
      onIncludeNetworksChange(initialParams.includeNetworks);
    }
  }, [initialParams]);

  useEffect(() => {
    setIsLoading(false);
  }, [initialParams]);

  useEffect(() => {
    if (isMobile) toggleMap(true);
  }, [isMobile]);

  return (
    <>
      {!isMobile && (
        <DesktopNavigation>
          <Header />
        </DesktopNavigation>
      )}

      <FiltersModal categories={categories} />

      <PageContainer
        bg="gray.50"
        pt={[0, 0, '5rem']}
        align="flex-start"
        minH="100vh"
      >
        <PageContent
          direction={['column', 'column', 'row']}
          align={['center', 'center', 'flex-start']}
          as={motion.div}
          animate={{ justifyContent: isMapOpen ? 'flex-end' : 'center' }}
          maxW={['full', 'full', 'unset']}
          py={0}
          px={0}
        >
          {isMobile && (
            <>
              <Collapse in={isOpen} startingHeight="50vh">
                <MapSection stores={stores.items}>
                  <ExpandControl isOpen={isOpen} onClick={onToggle} />
                </MapSection>
              </Collapse>

              <VStack
                w="full"
                bg="gray.50"
                mt="-0.5rem"
                pt="1rem"
                zIndex={2}
                borderTopRadius="xl"
                spacing="1.25rem"
              >
                <Box h="2px" w="2rem" bg="blue.800" />

                <Flex w="full" maxW={['37.5rem', 'unset']}>
                  <Search networks={slicedNetworks} stores={stores} />
                </Flex>
              </VStack>
            </>
          )}

          {!isMobile && (
            <>
              <Search networks={slicedNetworks} stores={stores} />

              <MapSection stores={stores.items} />
            </>
          )}
        </PageContent>
      </PageContainer>
    </>
  );
};
